/**
 * LoadingScreen — the one loading state for the whole app.
 *
 * The logo is drawn stroke by stroke — the triangle's three edges, the long
 * path line, and the low curve — each rendering in, in a RANDOM order every
 * cycle, then holding and gently fading before redrawing. Small and quiet by
 * design. Two shapes:
 *   fullScreen (default)  — covers the viewport (route guards, auth, share).
 *   fullScreen={false}    — a rounded panel inside DashboardLayout. Pages
 *                           seeded from pageCache should rarely show this —
 *                           if the data is already in memory, render it.
 */
import { useEffect, useMemo, useState } from 'react';

interface LoadingScreenProps {
  fullScreen?: boolean;
  /** Shorter plum panel for inline spots (e.g. inside a card). */
  compact?: boolean;
  label?: string;
}

// The five strokes of the mark, oriented so each draws "up" from its base:
// triangle right edge, triangle top edge, triangle bottom edge, the path
// (long slash), and the low curve.
const STROKES: { d: string; curve?: boolean }[] = [
  { d: 'M396 366 L396 44' },
  { d: 'M52 200 L396 44' },
  { d: 'M396 366 L52 200' },
  { d: 'M20 380 L424 20' },
  { d: 'M112 92 Q238 6 360 92', curve: true },
];

const CYCLE_MS = 2600;
const STAGGER_S = 0.14;

function shuffledOrder(): number[] {
  const idx = STROKES.map((_, i) => i);
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  return idx;
}

export function LoadingLogo({ label }: { label?: string }) {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCycle((c) => c + 1), CYCLE_MS);
    return () => clearInterval(t);
  }, []);
  // A fresh random draw order every cycle (cycle is the intended dep).
  const order = useMemo(() => shuffledOrder(), [cycle]);

  return (
    <div role="status" aria-live="polite" className="flex flex-col items-center gap-4">
      <svg
        key={cycle}
        viewBox="0 0 440 400"
        className="w-10 h-10 cp-logo-cycle overflow-visible"
        fill="none"
        aria-hidden="true"
      >
        {STROKES.map((s, i) => (
          <path
            key={i}
            d={s.d}
            className="cp-logo-stroke"
            style={{ animationDelay: `${order.indexOf(i) * STAGGER_S}s` }}
            stroke={s.curve ? 'rgba(255,255,255,0.5)' : 'white'}
            strokeWidth={s.curve ? 16 : 22}
            strokeLinecap="round"
          />
        ))}
      </svg>
      <span className={`font-sans text-sm text-white/60 ${label ? '' : 'sr-only'}`}>
        {label || 'Loading'}
      </span>
    </div>
  );
}

export default function LoadingScreen({ fullScreen = true, compact = false, label }: LoadingScreenProps) {
  if (fullScreen) {
    return (
      <div className="min-h-screen bg-[#4A2D5E] flex items-center justify-center">
        <LoadingLogo label={label} />
      </div>
    );
  }
  return (
    <div className={`bg-[#4A2D5E] rounded-[14px] flex items-center justify-center ${compact ? 'py-12' : 'min-h-[60vh]'}`}>
      <LoadingLogo label={label} />
    </div>
  );
}
